import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import HistoryItem from './components/HistoryItem';
import OriginalStateItem from './components/OriginalStateItem';
import './HistoryPanel.css';

/**
 * HistoryPanel - Displays undo/redo history (Photoshop-style)
 * 
 * Shows a single timeline from oldest (top) to newest (bottom).
 * The last item in the undo stack is highlighted as current.
 * Items above current = past actions (click to go back)
 * Items below current = undone actions, greyed out (click to redo forward)
 * 
 * GROUPED ACTIONS:
 * - Actions with isGroup=true are displayed as expandable items
 * - Click the expand button to see sub-actions
 * - Clicking the group undoes/redoes all sub-actions at once
 * 
 * This displays exactly what's in the undo/redo stacks - it's the real data.
 */
const HistoryPanel = ({ 
  undoStack = [], 
  redoStack = [], 
  onUndo, 
  onRedo, 
  onUndoTo,
  onRedoTo,
  onUndoAll,
  onClear,
  isOpen,
  onClose,
  sidebarCollapsed = false,
}) => {
  const contentRef = useRef(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Toggle group expansion
  const toggleGroup = (actionId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(actionId)) {
        newSet.delete(actionId);
      } else {
        newSet.add(actionId);
      }
      return newSet;
    });
  };

  // Auto-scroll to bottom when opened or when items change
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [isOpen, undoStack.length, redoStack.length]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Don't render when closed - save resources
  if (!isOpen) return null;

  // Click on "Original State" - undo everything
  const handleOriginalStateClick = () => {
    if (undoStack.length === 0) return; // Already at original
    if (onUndoAll) {
      onUndoAll();
    } else if (undoStack.length > 0) {
      // Fallback: undo to the first action
      onUndoTo(undoStack[0].id);
    }
  };

  const handleUndoItemClick = (action, index) => {
    // If clicking the last item in undo stack (current), do nothing
    if (index === undoStack.length - 1) {
      return;
    }
    // Otherwise, undo to the item AFTER this one (so this becomes current)
    const nextAction = undoStack[index + 1];
    if (nextAction) {
      onUndoTo(nextAction.id);
    }
  };

  const handleRedoItemClick = (action) => {
    onRedoTo(action.id);
  };

  const totalItems = undoStack.length + redoStack.length;
  
  // Redo stack is stored with most-recently-undone at the END
  // For display, we want chronological order: the action that was undone last should appear first
  // (because it's the next one to redo). So we reverse for display.
  const redoStackForDisplay = [...redoStack].reverse();

  // Render via portal to ensure proper positioning relative to sidebar
  return createPortal(
    <div className={`history-panel ${sidebarCollapsed ? 'history-panel--sidebar-collapsed' : ''}`}>
      <div className="history-panel__header">
        <h3 className="history-panel__title">History</h3>
        <div className="history-panel__actions">
          {totalItems > 0 && (
            <button 
              className="history-panel__clear-btn"
              onClick={onClear}
              title="Clear history"
            >
              Clear
            </button>
          )}
          <button 
            className="history-panel__close-btn"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      <div className="history-panel__content" ref={contentRef}>
        <div className="history-panel__timeline">
          {/* Original State - always shown at top */}
          <OriginalStateItem
            undoStackLength={undoStack.length}
            redoStackLength={redoStack.length}
            onClick={handleOriginalStateClick}
          />

          {/* Undo stack - oldest to newest, last one is "current" */}
          {undoStack.map((action, index) => {
            const isCurrent = index === undoStack.length - 1 && redoStack.length === 0;
            const isGroup = action.isGroup && action.subActions?.length > 1;
            const isExpanded = expandedGroups.has(action.id);
            
            return (
              <HistoryItem
                key={action.id || `undo-${index}`}
                action={action}
                index={index}
                isCurrent={isCurrent}
                isFuture={false}
                isGroup={isGroup}
                isExpanded={isExpanded}
                onToggleGroup={toggleGroup}
                onClick={() => handleUndoItemClick(action, index)}
                undoStackLength={undoStack.length}
              />
            );
          })}

          {/* Redo stack - displayed in chronological order (reversed from storage) */}
          {redoStackForDisplay.map((action, displayIndex) => {
            const isGroup = action.isGroup && action.subActions?.length > 1;
            const isExpanded = expandedGroups.has(action.id);
            
            return (
              <HistoryItem
                key={action.id || `redo-${displayIndex}`}
                action={action}
                index={displayIndex}
                isCurrent={false}
                isFuture={true}
                isGroup={isGroup}
                isExpanded={isExpanded}
                onToggleGroup={toggleGroup}
                onClick={() => handleRedoItemClick(action)}
                undoStackLength={undoStack.length}
              />
            );
          })}
        </div>
      </div>

      <div className="history-panel__footer">
        <span className="history-panel__count">
          {undoStack.length} undo • {redoStack.length} redo
        </span>
        <span className="history-panel__hint">
          Ctrl+Z / Ctrl+Y
        </span>
      </div>
    </div>,
    document.body
  );
};

export default HistoryPanel;
